import type { EChartsOption } from 'echarts';
import { useMemo } from 'react';

import { PALETTE, formatBucket } from 'entities/analytics/lib';
import { useRegistrations } from 'entities/analytics/model';
import { AnalyticsCard } from 'entities/analytics/ui/analytics-card';
import { EChart } from 'entities/analytics/ui/echart';

import { AnalyticsRangeParams } from 'shared/api';

type Props = { params: AnalyticsRangeParams };

export const RegistrationsCard = ({ params }: Props) => {
  const granularity = params.granularity ?? 'month';
  const { data, isLoading, isFetching } = useRegistrations(params);
  const series = useMemo(() => data?.data.series ?? [], [data]);
  const total = data?.data.total ?? 0;

  const option = useMemo<EChartsOption>(
    () => ({
      grid: { left: 8, right: 16, top: 12, bottom: 8, containLabel: true },
      tooltip: { trigger: 'axis' },
      xAxis: {
        type: 'category',
        data: series.map((p) => formatBucket(p.bucket, granularity)),
        axisLine: { lineStyle: { color: '#dfe5e3' } },
        axisLabel: { color: PALETTE.axis, fontSize: 10 },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        splitLine: { lineStyle: { color: PALETTE.grid } },
        axisLabel: { color: PALETTE.axis, fontSize: 10 },
      },
      series: [
        {
          name: 'Registrations',
          type: 'bar',
          data: series.map((p) => p.count),
          itemStyle: { color: PALETTE.primary, borderRadius: [3, 3, 0, 0] },
        },
      ],
    }),
    [series, granularity],
  );

  return (
    <AnalyticsCard
      title="Registrations"
      subtitle="New users by bucket"
      totals={[{ label: 'Total registrations', value: total.toLocaleString() }]}
      isLoading={isLoading}
      isFetching={isFetching}
      isEmpty={!isLoading && series.length === 0}
      chartHeight={210}
      chart={<EChart option={option} height={210} />}
    />
  );
};
